/**
 * ReplayMode — what the owner wakes up to.
 *
 * The pitch's sharpest line is that the owner should not see "通关率 37%" but
 * *where the stranger hesitated and how they flew*. So this screen is built
 * around three things and nothing else:
 *   1. one headline sentence about the person, not the room;
 *   2. the run itself, scrubable, with pins on every moment of doubt;
 *   3. a single button that turns watching into building again: 改一手.
 *
 * That third button is the whole retention argument. Watching must be one tap
 * from editing, or the loop stops here.
 */

import { decodeRoom } from '../core/codec'
import type { RoomDef } from '../core/room'
import { createFigure, GHOST_PALETTE } from '../render/character'
import type { Figure } from '../render/figure'
import { RoomView } from '../render/roomView'
import type { SceneView } from '../render/scene'
import { digestReplay, ReplayPlayer, type Replay } from '../sim/replay'
import type { RaidEvent } from '../sim/types'
import { button, el, formatSeconds } from '../ui/dom'

export interface ReplayModeDeps {
  scene: SceneView
  uiHost: HTMLElement
  replay: Replay
  /** Fallback room when the replay's inlined copy cannot be decoded. */
  room?: RoomDef | null
  onExit: () => void
  onEdit: (room: RoomDef) => void
  onRaid: (room: RoomDef) => void
}

const EVENT_LABELS: Partial<Record<RaidEvent['type'], string>> = {
  launched: '弹飞',
  swatted: '被拍',
  pushed: '被吹歪',
  slipped: '打滑',
  glued: '粘住',
  pitfall: '掉洞',
  decoy: '上了假宝箱的钩',
  bluffed: '相信了假陷阱',
  pickup: '拿到宝箱',
  escaped: '逃出去',
  failed: '失手',
  hesitate: '犹豫',
}

export class ReplayMode {
  private view: RoomView | null = null
  private ghost: Figure
  private player: ReplayPlayer
  private room: RoomDef | null
  private panel: HTMLDivElement
  private playhead = 0
  private playing = true
  private scrub!: HTMLInputElement
  private clockEl!: HTMLDivElement
  private captionEl!: HTMLDivElement
  private playBtn!: HTMLButtonElement
  private time = 0
  private captionUntil = 0

  constructor(private deps: ReplayModeDeps) {
    this.room = decodeRoom(deps.replay.roomCode) ?? deps.room ?? null
    this.player = new ReplayPlayer(deps.replay)
    this.ghost = createFigure(GHOST_PALETTE, { ghost: true, name: deps.replay.raiderName })
    deps.scene.scene.add(this.ghost.root)
    deps.scene.setMode('overview')

    if (this.room) {
      this.view = new RoomView(this.room)
      deps.scene.scene.add(this.view.root)
      deps.scene.setRoomSize(this.room.width, this.room.depth)
      const digest = digestReplay(deps.replay)
      this.view.setMarkers(digest.hesitations.map((h) => ({ x: h.x, z: h.z })))
    }

    this.panel = el('div', { class: 'replay-ui' })
    deps.uiHost.appendChild(this.panel)
    this.renderChrome()
  }

  private renderChrome(): void {
    const digest = digestReplay(this.deps.replay)
    const stats: string[] = []
    if (digest.launches > 0) stats.push(`弹飞 ${digest.launches} 次`)
    if (digest.pitfalls > 0) stats.push(`掉洞 ${digest.pitfalls} 次`)
    if (digest.hesitations.length > 0) stats.push(`犹豫 ${digest.hesitations.length} 次`)
    if (digest.fellForDecoy) stats.push('上了假宝箱的钩')
    if (digest.respectedBluff) stats.push('绕开了你的假陷阱')
    stats.push(`${digest.seconds.toFixed(1)} 秒`)

    this.captionEl = el('div', { class: 'caption' })
    this.clockEl = el('div', { class: 'replay-clock' })
    this.scrub = el('input', {
      class: 'scrub',
      type: 'range',
      min: '0',
      max: '1000',
      value: '0',
      oninput: () => {
        this.playhead = (Number(this.scrub.value) / 1000) * this.player.duration
        this.player.reset()
        this.playing = false
        this.playBtn.textContent = '▶'
      },
    }) as HTMLInputElement
    this.playBtn = button('⏸', () => this.togglePlay(), 'btn icon')

    const timeline = el('div', { class: 'replay-timeline' }, [
      this.playBtn,
      this.scrub,
      this.clockEl,
    ])

    const chips = el(
      'div',
      { class: 'replay-chips' },
      stats.map((s) => el('span', { class: 'stat-chip', text: s })),
    )

    const beats = el(
      'div',
      { class: 'replay-beats' },
      digest.triggered.slice(0, 8).map((t) =>
        el('div', { class: 'beat' }, [
          el('span', { class: 'beat-time', text: `${t.t.toFixed(1)}s` }),
          el('span', { class: 'beat-label', text: EVENT_LABELS[t.type] ?? t.type }),
        ]),
      ),
    )

    this.panel.append(
      el('div', { class: 'replay-top' }, [
        button('← 返回', () => this.deps.onExit(), 'btn ghost small'),
        el('div', {
          class: 'replay-outcome',
          text: this.deps.replay.outcome === 'escaped' ? '他带走了宝箱' : '他空手离开',
        }),
      ]),
      el('div', { class: 'replay-headline', text: digest.headline }),
      chips,
      this.captionEl,
      beats,
      el('div', { class: 'replay-cta' }, [
        this.room
          ? button('再让人来一次', () => this.deps.onRaid(this.room!), 'btn ghost')
          : null,
        this.room ? button('改一手', () => this.deps.onEdit(this.room!), 'btn primary') : null,
      ].filter(Boolean) as HTMLElement[]),
      timeline,
    )
  }

  private togglePlay(): void {
    this.playing = !this.playing
    this.playBtn.textContent = this.playing ? '⏸' : '▶'
  }

  update(dt: number): void {
    this.time += dt
    if (this.playing) {
      this.playhead += dt
      if (this.playhead >= this.player.duration) {
        this.playhead = this.player.duration
        this.playing = false
        this.playBtn.textContent = '▶'
      }
      this.scrub.value = String(
        Math.round((this.playhead / Math.max(this.player.duration, 0.001)) * 1000),
      )
    }

    const state = this.player.sample(this.playhead)
    this.ghost.update(state, dt)
    this.view?.update(this.time)
    this.view?.setChestVisible(!state.carrying)
    this.deps.scene.update(dt, state)

    for (const e of this.player.drain(this.playhead)) {
      const label = EVENT_LABELS[e.type]
      if (!label) continue
      this.captionEl.textContent = label
      this.captionEl.classList.remove('pop')
      void this.captionEl.offsetWidth
      this.captionEl.classList.add('pop')
      this.captionUntil = this.time + 1.4
    }
    if (this.time > this.captionUntil) this.captionEl.textContent = ''

    this.clockEl.textContent = `${formatSeconds(this.playhead)} / ${this.player.duration.toFixed(1)}s`
  }

  dispose(): void {
    this.ghost.dispose()
    this.view?.dispose()
    this.panel.remove()
  }
}
